import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { ToggleButton } from 'components/Input/ToggleButton'
import { Input } from 'components/Input/Input'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { NavBar } from 'components/NavBar/NavBar'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { ISelectValue } from 'constants/globalTypes'
import { ISiteNetworkOption } from 'types/siteNetwork.interface'
import {
  activateDrafts,
  calculateRoute,
  cancelRoadEvent,
  clearSiteNetwork,
  getNetworkDraftGraph,
  getNetworkGraph,
  getRoadEvents,
  getSiteNetworkLocations,
  importDrawnSegment,
} from 'features/siteNetwork/siteNetworkSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { getParams } from 'utils/helpers'
import { tokens } from 'styling/tokens'
import { Button, Flex, MainHeadline, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, TitleText } from 'styling/Typography'
import { CloseRoadModal } from './CloseRoadModal'
import { NetworkMap } from './NetworkMap'
import {
  defaultRoadClassColor,
  roadClassColors,
  roadClassOptions,
  vehicleOptions,
} from './siteNetworkData'
import {
  EventRow,
  Hint,
  KV,
  Layout,
  LegendDot,
  LegendRow,
  SidePanel,
} from './SiteNetwork.styled'

const fmtMin = (s: number): string =>
  `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')} min`

const countLineStrings = (features?: { geometry: { type: string } }[]): number =>
  features?.filter((f) => f.geometry.type === 'LineString').length ?? 0

export const SiteNetwork = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { graph, draftGraph, locations, roadEvents, route, isRouting } = useSelector(
    (state: RootState) => state.siteNetwork,
  )

  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [vehicle, setVehicle] = useState(String(vehicleOptions[0].value))

  const [mode, setMode] = useState<'view' | 'draw'>('view')
  const [cls, setCls] = useState(String(roadClassOptions[0].value))
  const [name, setName] = useState('')
  const [oneWay, setOneWay] = useState(false)
  const [pts, setPts] = useState<[number, number][]>([])

  const [closeModal, setCloseModal] = useState<{
    segmentId: string
    segmentName: string | null
  } | null>(null)

  const [params] = useState(getParams({ location, limit: 30 }))

  useQueryPagination({
    fetchFunctions: [],
    fetchFunctionsNoParams: [
      getNetworkGraph,
      getNetworkDraftGraph,
      getSiteNetworkLocations,
      getRoadEvents,
    ],
    cleanUpFunctions: [clearSiteNetwork],
    params,
  })

  const closedIds = useMemo(
    () =>
      roadEvents
        .filter((e) => e.type === 'CLOSED' && e.segment_id)
        .map((e) => e.segment_id as string),
    [roadEvents],
  )

  const segmentCount = countLineStrings(graph?.features)
  const draftCount = countLineStrings(draftGraph?.features)

  const locationOptions: ISiteNetworkOption[] = locations.map((l) => ({
    label: l.name ?? l.id,
    value: l.id,
  }))

  const runRoute = () => {
    if (!origin || !destination) return
    const [machineClass, loadState] = vehicle.split('|')
    dispatch(
      calculateRoute({
        origin: { locationId: origin },
        destination: { locationId: destination },
        machineClass,
        loadState,
      }),
    )
  }

  const toggleClosure = (segmentId: string, segmentName: string | null) => {
    const existing = roadEvents.find((e) => e.segment_id === segmentId && e.type === 'CLOSED')
    if (existing) {
      dispatch(cancelRoadEvent(existing.id))
      return
    }
    setCloseModal({ segmentId, segmentName })
  }

  const finishDraw = (last: [number, number]) => {
    const coordinates: [number, number][] = [...pts, last]
    if (coordinates.length >= 2) {
      dispatch(importDrawnSegment({ coordinates, cls, name: name || undefined, oneWay }))
      setPts([])
      setName('')
    } else {
      setPts([])
    }
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex between center mb='16'>
          <MainHeadline>Site Network</MainHeadline>
        </Flex>
        <Layout>
          <NetworkMap
            graph={graph}
            draftGraph={draftGraph}
            closedSegmentIds={closedIds}
            routeGeometry={route?.geometry ?? null}
            drawCoordinates={pts}
            mode={mode}
            onSegmentClick={toggleClosure}
            onMapClick={(c) => mode === 'draw' && setPts((prev) => [...prev, c])}
            onMapDoubleClick={(c) => mode === 'draw' && finishDraw(c)}
          />
          <SidePanel>
            <Container>
              <ContainerHeader>
                <TitleText weight='600'>Network</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.5rem'>
                <KV>
                  <span>Active segments</span>
                  <b>{segmentCount}</b>
                </KV>
                <KV>
                  <span>Locations anchored</span>
                  <b>{locations.length}</b>
                </KV>
                <KV>
                  <span>Active road events</span>
                  <b>{roadEvents.length}</b>
                </KV>
                <LegendRow>
                  <LegendDot $color={roadClassColors.HAUL_PRIMARY} /> Haul
                  <LegendDot $color={defaultRoadClassColor} /> Service
                  <LegendDot $color={tokens.status.danger.fg} /> Closed
                </LegendRow>
              </ContainerContent>
            </Container>

            <Container>
              <ContainerHeader>
                <TitleText weight='600'>Editor</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.5rem'>
                <TabsContainer>
                  <Tabs
                    active={mode === 'view'}
                    onClick={() => {
                      setMode('view')
                      setPts([])
                    }}
                  >
                    View
                  </Tabs>
                  <Tabs active={mode === 'draw'} onClick={() => setMode('draw')}>
                    Draw road
                  </Tabs>
                </TabsContainer>
                {mode === 'draw' && (
                  <>
                    <HonestSelectInput
                      nomarg
                      labelText='Road class'
                      defaultValue={cls}
                      options={roadClassOptions}
                      onChange={(value: ISelectValue) => setCls(String(value.value))}
                    />
                    <Input
                      nomarg
                      labelText='Name (optional)'
                      placeholder='e.g. Ramp 3'
                      value={name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                    />
                    <ToggleButton
                      start
                      label='One-way (in draw direction)'
                      state={oneWay}
                      handleChange={() => setOneWay((prev) => !prev)}
                    />
                    <Hint>
                      Click the map to place vertices; double-click to finish. Endpoints snap to
                      existing nodes within 30 m server-side. Switch to View to discard.
                    </Hint>
                  </>
                )}
                <KV>
                  <span>Draft segments</span>
                  <b>{draftCount}</b>
                </KV>
                <Button
                  orange
                  width100
                  disabled={draftCount === 0}
                  onClick={() => dispatch(activateDrafts())}
                >
                  Activate drafts
                </Button>
                <Hint>Drafts are not routable until activated.</Hint>
              </ContainerContent>
            </Container>

            <Container>
              <ContainerHeader>
                <TitleText weight='600'>Route tester</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.5rem'>
                <HonestSelectInput
                  nomarg
                  labelText='Origin'
                  defaultValue={origin}
                  options={locationOptions}
                  onChange={(value: ISelectValue) => setOrigin(String(value.value))}
                />
                <HonestSelectInput
                  nomarg
                  labelText='Destination'
                  defaultValue={destination}
                  options={locationOptions}
                  onChange={(value: ISelectValue) => setDestination(String(value.value))}
                />
                <HonestSelectInput
                  nomarg
                  labelText='Vehicle'
                  defaultValue={vehicle}
                  options={vehicleOptions}
                  onChange={(value: ISelectValue) => setVehicle(String(value.value))}
                />
                <Button
                  orange
                  width100
                  disabled={!origin || !destination || isRouting}
                  onClick={runRoute}
                >
                  {isRouting ? 'Routing…' : 'Get route'}
                </Button>
                {route && (
                  <>
                    <MetricCard
                      label='Expected (p50)'
                      value={fmtMin(route.durationS.p50)}
                      accentColor={tokens.accent.base}
                    />
                    <MetricCard
                      label='Plan-safe (p85)'
                      value={fmtMin(route.durationS.p85)}
                    />
                    <MetricCard
                      label='Distance'
                      value={`${(route.distanceM / 1000).toFixed(2)} km`}
                    />
                    <MetricCard label='Confidence' value={route.confidence.level} />
                  </>
                )}
                <Hint>
                  Times come from learned per-segment speeds where enough GPS traversals exist;
                  physics defaults elsewhere.
                </Hint>
              </ContainerContent>
            </Container>

            <Container>
              <ContainerHeader>
                <TitleText weight='600'>Active road events</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.5rem'>
                {roadEvents.length === 0 && (
                  <Hint>No closures or restrictions. Click a road on the map to close it.</Hint>
                )}
                {roadEvents.map((e) => (
                  <EventRow key={e.id}>
                    <Flex center gap='0.5rem'>
                      <StatusPill status={e.type === 'CLOSED' ? 'danger' : 'warning'}>
                        {e.type}
                      </StatusPill>
                      <BodyText>{e.reason}</BodyText>
                    </Flex>
                    <Button small onClick={() => dispatch(cancelRoadEvent(e.id))}>
                      Reopen
                    </Button>
                  </EventRow>
                ))}
              </ContainerContent>
            </Container>
          </SidePanel>
        </Layout>
      </PageWrapper>
      {closeModal && (
        <CloseRoadModal
          segmentId={closeModal.segmentId}
          segmentName={closeModal.segmentName}
          closeModal={() => setCloseModal(null)}
        />
      )}
    </>
  )
}
